/**
 * ArrayEditor Component
 *
 * Generic array CRUD component with add/remove/reorder functionality.
 * Follows SOLID principles for maximum reusability across wizard and inline edit.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard (Component Extraction)
 * Usage: Highlights, gear lists, checklists, any array field
 */

import { useState, useCallback } from 'react';

/**
 * Default icons (can be overridden via props)
 */
const DefaultIcons = {
  Remove: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  MoveUp: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ),
  MoveDown: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  ),
  Add: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  )
};

/**
 * ArrayEditor Component
 *
 * @param {Array} items - Current array items
 * @param {Function} onChange - Callback when array changes: (newItems) => void
 * @param {Function} renderItem - Render function for item display: (item, index) => ReactNode
 * @param {Function} renderAddForm - Optional custom add form component
 * @param {Number} maxItems - Maximum allowed items (0 = unlimited)
 * @param {Number} minItems - Minimum required items
 * @param {Boolean} allowReorder - Enable up/down buttons (default: true)
 * @param {Boolean} showCount - Show item count (default: true)
 * @param {String} addButtonText - Text for add button (default: "Add")
 * @param {String} addPlaceholder - Placeholder for simple string input
 * @param {String} emptyMessage - Message when array is empty
 * @param {String} itemClassName - CSS class for item container
 * @param {Object} icons - Custom icon components
 */
export default function ArrayEditor({
  items = [],
  onChange,
  renderItem,
  renderAddForm,
  maxItems = 0,
  minItems = 0,
  allowReorder = true,
  showCount = true,
  addButtonText = 'Add',
  addPlaceholder = 'Add an item...',
  emptyMessage = 'No items yet.',
  itemClassName = '',
  icons = DefaultIcons,
  ...props
}) {
  const [newItemValue, setNewItemValue] = useState('');

  // Memoized handlers to prevent unnecessary re-renders
  const handleRemove = useCallback((index) => {
    // Prevent removal if at minimum
    if (minItems > 0 && items.length <= minItems) {
      return;
    }

    const updated = items.filter((_, i) => i !== index);
    onChange(updated);
  }, [items, minItems, onChange]);

  const handleMoveUp = useCallback((index) => {
    // Early return for edge cases
    if (index === 0) {return;}

    const updated = [...items];
    // Swap with previous item
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated);
  }, [items, onChange]);

  const handleMoveDown = useCallback((index) => {
    // Early return for edge cases
    if (index === items.length - 1) {return;}

    const updated = [...items];
    // Swap with next item
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    // eslint-disable-next-line security/detect-object-injection -- safe: index is controlled array index from UI interaction
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated);
  }, [items, onChange]);

  const handleAdd = useCallback((value) => {
    // Validation checks (hoist boolean logic)
    const isAtMax = maxItems > 0 && items.length >= maxItems;
    const isEmpty = !value || (typeof value === 'string' && !value.trim());

    if (isAtMax || isEmpty) {return;}

    const updated = [...items, value];
    onChange(updated);
    setNewItemValue('');
  }, [items, maxItems, onChange]);

  const handleSimpleAdd = useCallback((  _e) => {
    _e.preventDefault();
    handleAdd(newItemValue.trim());
  }, [newItemValue, handleAdd]);

  // Compute derived state once
  const isAtMaxItems = maxItems > 0 && items.length >= maxItems;
  const canRemove = minItems === 0 || items.length > minItems;

  return (
    <div className="space-y-3" {...props}>
      {/* Item count indicator */}
      {showCount && items.length > 0 && (
        <div className="text-sm text-gray-600">
          {items.length} {items.length === 1 ? 'item' : 'items'}
          {maxItems > 0 && ` (max ${maxItems})`}
          {minItems > 0 && ` (min ${minItems} required)`}
        </div>
      )}

      {/* Items list */}
      {items.length === 0 ? (
        <div className="text-sm text-gray-500 italic py-4 text-center bg-gray-50 rounded-lg border border-gray-200">
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => {
            const isFirst = index === 0;
            const isLast = index === items.length - 1;

            return (
              <div
                key={index}
                className={`flex items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200 ${itemClassName}`}
              >
                {/* Item content (custom render) */}
                <div className="flex-1 min-w-0">
                  {renderItem ? renderItem(item, index) : (
                    <span className="text-sm text-gray-700">{String(item)}</span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Reorder buttons */}
                  {allowReorder && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleMoveUp(index)}
                        disabled={isFirst}
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                        aria-label={`Move item ${index + 1} up`}
                      >
                        <icons.MoveUp />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(index)}
                        disabled={isLast}
                        className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                        aria-label={`Move item ${index + 1} down`}
                      >
                        <icons.MoveDown />
                      </button>
                    </>
                  )}

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={!canRemove}
                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title={canRemove ? "Remove" : `Minimum ${minItems} required`}
                    aria-label={`Remove item ${index + 1}`}
                  >
                    <icons.Remove />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      {!isAtMaxItems && (
        <div className="pt-2 border-t border-gray-200">
          {renderAddForm ? (
            // Custom add form
            renderAddForm({ onAdd: handleAdd, disabled: isAtMaxItems })
          ) : (
            // Default simple string input
            <form onSubmit={handleSimpleAdd} className="flex gap-2">
              <input
                type="text"
                value={newItemValue}
                onChange={(e) => setNewItemValue(e.target.value)}
                placeholder={addPlaceholder}
                disabled={isAtMaxItems}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="New item input"
              />
              <button
                type="submit"
                disabled={!newItemValue.trim() || isAtMaxItems}
                className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5 font-medium"
                aria-label="Add item"
              >
                <icons.Add />
                {addButtonText}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Preset configurations for common use cases
 */
export const ArrayEditorPresets = {
  // Simple string list (highlights, bullet points)
  stringList: {
    maxItems: 8,
    minItems: 0,
    allowReorder: true,
    renderItem: (item) => <span className="text-sm text-gray-700">{item}</span>
  },

  // Checklist items (what's included, what to bring)
  checklist: {
    maxItems: 20,
    minItems: 0,
    allowReorder: false,
    renderItem: (item) => (
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span className="text-sm text-gray-700">{item}</span>
      </div>
    )
  }
};
